import { Button, ButtonProps } from '@design-system/button';
import { QuantityButton } from '@design-system/quantity-button';
import { Typography } from '@design-system/typography';
import classNames from 'classnames';
import React, { FC, useContext, useState } from 'react';

import { StoreContext } from '~/context/shopify';
import { formatPrice } from '~/utils/currencyFormatting';

import { ShopifyBlockProps } from './models';
import NotificationModal from './NotificationModal';

const addToCartButtonWithoutStorefrontId = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  storefrontId,
  ...rest
}: ShopifyBlockProps['addToCartButton']): ButtonProps => rest;

// eslint-disable-next-line complexity, max-statements
const ShopifyBlock: FC<ShopifyBlockProps> = ({
  addToCartButton,
  buyNowButton,
  addToCartModal,
  quantityButton,
  availableForSale,
  currentPrice,
  discountedPrice,
  currentPriceUI,
  discountedPriceUI,
  className,
  description,
  isProductPageHeader,
  isUpSellVariant,
  availableQuantity,
  inventoryQuantity,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const {
    addVariantToCart,
    addVariantToCartByBuyNow,
    checkout,
    hasAddToCartError,
    setHasAddToCartError,
    loadingProductID,
  } = useContext(StoreContext);

  const [quantityValue, setQuantityValue] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reset, setReset] = useState(false);

  const variantCartQuantity = Number(
    checkout.lineItems.find(
      (item) => item.variant.id === addToCartButton.storefrontId
    )?.quantity || 0
  );

  const currentMaxQuantity = inventoryQuantity - variantCartQuantity;

  const [maxQuantity, setMaxQuantity] = useState(currentMaxQuantity);
  const [availableVariantQuantity, setAvailableVariantQuantity] =
    useState(maxQuantity);

  const handleQuantityChange = (value: number) => {
    setAvailableVariantQuantity(maxQuantity - value);
    setQuantityValue(value);
    setReset(false);
  };

  const handleCloseModal = () => {
    if (setHasAddToCartError) {
      setHasAddToCartError(false);
    }

    setIsModalOpen(false);
  };

  const addToCartModalExt = {
    ...addToCartModal,
    itemsInCart: checkout?.lineItems?.reduce(
      (acc, { quantity }) => acc + quantity,
      0
    ),
    totalPrice: formatPrice(
      checkout?.totalPriceV2?.currencyCode,
      Number(checkout?.totalPriceV2?.amount)
    ),
  };

  const setCustomAttributes = () => [
    { key: 'description', value: description },
  ];

  const handleAddToCart = () => {
    addVariantToCart(
      addToCartButton.storefrontId,
      quantityValue,
      setCustomAttributes()
    );
    setIsModalOpen(!isModalOpen);
    setReset(!reset);
    if (!hasAddToCartError) {
      setMaxQuantity(availableVariantQuantity);
    }
  };

  const handleBuyNow = () => {
    addVariantToCartByBuyNow(
      addToCartButton.storefrontId,
      quantityValue,
      setCustomAttributes()
    );
  };

  const isProductPageHeaderOrUpSell = isProductPageHeader || isUpSellVariant;
  const shouldRenderDiscountedPrice =
    discountedPrice && discountedPriceUI && isProductPageHeaderOrUpSell;
  const shouldRenderPriceContainer =
    shouldRenderDiscountedPrice || isProductPageHeaderOrUpSell;

  return (
    <div
      className={classNames('shopify-block', className, {
        'shopify-block--up-sell': isUpSellVariant,
      })}
    >
      {shouldRenderPriceContainer ? (
        <div className="shopify-block__price-container">
          {shouldRenderDiscountedPrice ? (
            <Typography
              className="shopify-block__price shopify-block__price--discounted"
              {...discountedPriceUI}
              content={discountedPrice}
            />
          ) : null}
          <Typography
            className="shopify-block__price"
            {...currentPriceUI}
            content={currentPrice}
          />
        </div>
      ) : null}
      {availableQuantity && isProductPageHeader ? (
        <div className="shopify-block__available-quantity-wrapper">
          <Typography
            className="shopify-block__available-quantity"
            variant="headline"
            size="xs"
            content={`${availableQuantity} ${availableVariantQuantity}`}
            tag="p"
            weight="regular"
          />
        </div>
      ) : null}
      <div className="shopify-block__action-buttons">
        {availableForSale && quantityButton && isProductPageHeader ? (
          <QuantityButton
            className="shopify-block__quantity"
            {...quantityButton}
            value={quantityValue}
            onChange={handleQuantityChange}
            max={maxQuantity}
            min={maxQuantity ? quantityButton.min : 0}
            reset={reset}
          />
        ) : null}
        <div className="shopify-block__cart-buttons">
          <Button
            className="shopify-block__add-to-cart"
            isLoading={addToCartButton.storefrontId === loadingProductID}
            {...addToCartButtonWithoutStorefrontId(addToCartButton)}
            onClick={handleAddToCart}
            disabled={!availableForSale || !maxQuantity}
          />
          {buyNowButton && availableForSale ? (
            <Button
              className="shopify-block__buy-now-button"
              {...buyNowButton}
              onClick={handleBuyNow}
            />
          ) : null}
        </div>
        {addToCartModal ? (
          <NotificationModal
            {...{
              addToCartModal: addToCartModalExt,
              isModalOpen: isModalOpen && !loadingProductID,
              setIsModalOpen,
              currentPrice,
              quantityValue,
              hasAddToCartError: !!hasAddToCartError,
              handleCloseModal,
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ShopifyBlock;
